import React from 'react';

import Box from '@mui/material/Box';

import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import ContactMailOutlined from '@mui/icons-material/ContactMailOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';

import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import { FormInput, ButtonRow, useTrackedFormik } from '@omnigenbiodata/ui';

import { POSTAL_CODE_REGEX, TELEPHONE_NUMBER_REGEX } from '../../../../../../core/constants/forms.constant';
import { chartColours } from '../../../../../../core/constants/colours.constants';
import PageSection from '../../../../../../components/content/PageSection';

interface InitialValues {
  address: string;
  sk2: string;
  landline: string;
  firstName: string;
  surname: string;
  postcode: string;
}

interface DetailsUpdateFormProps {
  onSubmit: (values: InitialValues) => void;
  initialValues: InitialValues;
}

function DetailsUpdateForm({ onSubmit, initialValues }: DetailsUpdateFormProps) {
  const { t } = useTranslation(['portal', 'validation']);

  const validationSchema = yup.object({
    address: yup.string().test(
      'oneOfRequired',
      t('addressRequired', {
        ns: 'validation',
      }),
      function () {
        return this.parent.postcode || this.parent.address;
      },
    ),
    landline: yup.string().matches(TELEPHONE_NUMBER_REGEX, t('landlineTelephoneFormat', { ns: 'validation' })),
    firstName: yup.string().required(
      t('firstNameRequired', {
        ns: 'validation',
      }),
    ),
    surname: yup.string().required(
      t('lastNameRequired', {
        ns: 'validation',
      }),
    ),
    postcode: yup
      .string()
      .test(
        'oneOfRequired',
        t('addressRequired', {
          ns: 'validation',
        }),
        function () {
          return this.parent.postcode || this.parent.address;
        },
      )
      .matches(POSTAL_CODE_REGEX, t('postalCodeFormat', { ns: 'validation' })),
  });

  const formik = useTrackedFormik(
    {
      initialValues,
      validationSchema,
      onSubmit,
    },
    'personal-details',
  );

  return (
    <Box
      sx={{
        '& form': {
          width: '100%',
        },
        '& label': {
          fontWeight: 800,
        },
        '& .MuiInputBase-root': {
          borderRadius: 2,
        },
        '& button': {
          borderRadius: 999,
          fontWeight: 900,
          textTransform: 'none',
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <PageSection title={t('personal.labels.hospitalNum')} colour={chartColours[1]} icon={<BadgeOutlined />}>
          <FormInput
            label={t('personal.labels.hospitalNum')}
            value={formik.values.sk2}
            error={formik.errors.sk2}
            touched={formik.touched.sk2}
            name="hospitalNum"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            startAdornment={<strong>KZ</strong>}
          />
        </PageSection>

        <PageSection title={t('personal.title')} colour={chartColours[2]} icon={<PersonOutlineOutlined />}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <FormInput
              label={t('personal.labels.firstName')}
              value={formik.values.firstName}
              error={formik.errors.firstName}
              touched={formik.touched.firstName}
              name="firstName"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <FormInput
              label={t('personal.labels.surname')}
              value={formik.values.surname}
              error={formik.errors.surname}
              touched={formik.touched.surname}
              name="surname"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>

          <FormInput
            label={t('personal.labels.landline')}
            value={formik.values.landline}
            error={formik.errors.landline}
            touched={formik.touched.landline}
            name="landline"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </PageSection>

        <PageSection title={t('personal.labels.address')} colour={chartColours[3]} icon={<ContactMailOutlined />}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
              gap: 2,
            }}
          >
            <FormInput
              label={t('personal.labels.postcode')}
              value={formik.values.postcode}
              error={formik.errors.postcode}
              touched={formik.touched.postcode}
              name="postcode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            <FormInput
              label={t('personal.labels.address')}
              value={formik.values.address}
              error={formik.errors.address}
              touched={formik.touched.address}
              name="address"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Box>
        </PageSection>

        <Box
          sx={{
            pt: 0.5,
          }}
        >
          <ButtonRow forwardLabel={t('personal.button')} showBack={false} forwardColor="primary" buttonSize="large" />
        </Box>
      </form>
    </Box>
  );
}

export default DetailsUpdateForm;
